import { useState } from 'react'
import type { WebhookDelivery, WebhookEndpoint } from '../api/types'
import {
  useCreateWebhook,
  useDeleteWebhook,
  useRotateWebhookSecret,
  useTestWebhook,
  useUpdateWebhook,
  useWebhookDeliveries,
  useWebhookEvents,
  useWebhooks,
} from '../hooks/useWebhooks'
import { timeAgo } from '../utils/format'
import { Badge, Button, Card, ErrorNote, Field, LoadingBlock, SecretNotice, type Tone } from './ui'

const DELIVERY: Record<WebhookDelivery['status'], { tone: Tone; label: string }> = {
  ready: { tone: 'warn', label: 'Waiting' },
  leased: { tone: 'warn', label: 'Sending' },
  succeeded: { tone: 'ok', label: 'Delivered' },
  failed: { tone: 'danger', label: 'Failed' },
  cancelled: { tone: 'neutral', label: 'Cancelled' },
}

/** Groups for the event picker, by the part of an event's name before the dot. */
const EVENT_GROUPS: [string, string][] = [
  ['episode', 'Episodes'],
  ['transcript', 'Transcripts and text'],
  ['text', 'Transcripts and text'],
  ['clip', 'Clips'],
  ['live', 'Live'],
]

function groupOf(event: string): string {
  const prefix = event.split('.')[0]
  return EVENT_GROUPS.find(([key]) => key === prefix)?.[1] ?? 'Other'
}

/** Where indiefuture sends signed events: add, test, turn off, rotate and delete endpoints. */
export function WebhooksCard({ workspaceId }: { workspaceId: string }) {
  const endpoints = useWebhooks(workspaceId)
  const [secret, setSecret] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const none = endpoints.data?.length === 0
  return (
    <Card title="Webhooks" actions={!adding && !none && <Button variant="secondary" onClick={() => setAdding(true)}>Add endpoint</Button>}>
      <div className="stack">
        <p className="muted">Your server gets a signed POST the moment something happens here: an episode is ready, a transcript is saved, a stream goes live.</p>
        {secret && <SecretNotice secret={secret} message="Copy this signing secret now. It will not be shown again." onDone={() => setSecret(null)} />}
        {adding ? (
          <NewEndpoint
            workspaceId={workspaceId}
            onCancel={() => setAdding(false)}
            onSecret={(made) => {
              setSecret(made)
              setAdding(false)
            }}
          />
        ) : (
          none && (
            <div className="webhooks-empty">
              <span className="muted">No endpoints yet.</span>
              <Button onClick={() => setAdding(true)}>Add endpoint</Button>
            </div>
          )
        )}
        {endpoints.isPending && <LoadingBlock />}
        {endpoints.isError && <ErrorNote error={endpoints.error} />}
        {endpoints.data && endpoints.data.length > 0 && (
          <div className="webhook-list">
            {endpoints.data.map((endpoint) => (
              <EndpointRow key={endpoint.id} workspaceId={workspaceId} endpoint={endpoint} onSecret={setSecret} />
            ))}
          </div>
        )}
        <details className="webhooks-help">
          <summary>How to check a request is from indiefuture</summary>
          <p className="muted small">
            Each request carries an <code>Indiefuture-Signature</code> header, <code>t=…,v1=…</code>. Compute an HMAC-SHA256 of <code>t.body</code> (the
            timestamp, a dot, then the raw body) with the endpoint's secret and compare it with <code>v1</code>. Refuse a timestamp more than five
            minutes old, and use the event's <code>id</code> to skip repeats.
          </p>
        </details>
      </div>
    </Card>
  )
}

function NewEndpoint({ workspaceId, onSecret, onCancel }: { workspaceId: string; onSecret: (secret: string) => void; onCancel: () => void }) {
  const events = useWebhookEvents()
  const create = useCreateWebhook(workspaceId)
  const [url, setUrl] = useState('')
  const [every, setEvery] = useState(true)
  const [chosen, setChosen] = useState<string[]>([])
  const subscribable = events.data?.filter((e) => e.name !== 'webhook.test') ?? []
  const groups = [...new Set(subscribable.map((e) => groupOf(e.name)))]
  const toggle = (name: string, on: boolean) => setChosen((now) => (on ? [...now, name] : now.filter((n) => n !== name)))
  return (
    <form
      className="webhook-form"
      onSubmit={(event) => {
        event.preventDefault()
        create.mutate({ url: url.trim(), events: every ? [] : chosen, description: '' }, { onSuccess: (created) => onSecret(created.secret) })
      }}
    >
      <Field label="Endpoint URL" hint="An https address on your server that accepts POST requests.">
        <input id="webhook-url" type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://yoursite.com/hooks/indiefuture" required />
      </Field>
      <fieldset className="webhook-events">
        <legend className="field-label">Send</legend>
        <label className="check">
          <input type="radio" name="webhook-scope" checked={every} onChange={() => setEvery(true)} /> Every event
        </label>
        <label className="check">
          <input type="radio" name="webhook-scope" checked={!every} onChange={() => setEvery(false)} /> Only the events I choose
        </label>
        {!every && (
          <div className="event-groups">
            {groups.map((group) => (
              <div key={group} className="event-group">
                <span className="event-group-title">{group}</span>
                {subscribable
                  .filter((e) => groupOf(e.name) === group)
                  .map((e) => (
                    <label key={e.name} className="option-row">
                      <input type="checkbox" checked={chosen.includes(e.name)} onChange={(ev) => toggle(e.name, ev.target.checked)} />
                      <span>
                        <code>{e.name}</code>
                        <span className="muted small">{(e.description.split(':')[0] ?? '').replaceAll('`', '')}</span>
                      </span>
                    </label>
                  ))}
              </div>
            ))}
          </div>
        )}
      </fieldset>
      {create.error && <ErrorNote error={create.error} />}
      <div className="row">
        <Button type="submit" busy={create.isPending} disabled={!url.trim() || (!every && chosen.length === 0)}>Add endpoint</Button>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  )
}

function EndpointRow({ workspaceId, endpoint, onSecret }: { workspaceId: string; endpoint: WebhookEndpoint; onSecret: (secret: string) => void }) {
  const [open, setOpen] = useState(false)
  const update = useUpdateWebhook(workspaceId)
  const remove = useDeleteWebhook(workspaceId)
  const rotate = useRotateWebhookSecret(workspaceId)
  const test = useTestWebhook(workspaceId)
  const error = update.error ?? remove.error ?? rotate.error ?? test.error
  return (
    <div className="webhook stack-sm">
      <div className="webhook-top">
        <span className={`status-dot ${!endpoint.enabled ? 'off' : endpoint.last_status === 'failed' ? 'bad' : endpoint.last_status ? 'good' : ''}`} aria-hidden="true" />
        <div className="webhook-main">
          <code className="webhook-url">{endpoint.url}</code>
          <div className="webhook-meta">
            {endpoint.events.length === 0 ? <span className="tag">Every event</span> : endpoint.events.map((e) => <span key={e} className="tag">{e}</span>)}
            <span className="muted small">
              {!endpoint.enabled ? 'Turned off' : endpoint.last_delivery_at ? `Last sent ${timeAgo(endpoint.last_delivery_at)}` : 'Nothing sent yet'}
            </span>
            {endpoint.enabled && endpoint.last_status && (
              <Badge tone={endpoint.last_status === 'succeeded' ? 'ok' : 'danger'}>{endpoint.last_status === 'succeeded' ? 'Delivering' : 'Failing'}</Badge>
            )}
          </div>
        </div>
      </div>
      {endpoint.last_status === 'failed' && endpoint.last_error && <span className="small warn-text">{endpoint.last_error}</span>}
      <div className="row">
        <Button
          variant="secondary"
          busy={test.isPending}
          disabled={!endpoint.enabled}
          onClick={() => test.mutate(endpoint.id, { onSuccess: () => setOpen(true) })}
        >
          Send test
        </Button>
        <Button variant="ghost" onClick={() => setOpen((now) => !now)}>{open ? 'Hide deliveries' : 'Deliveries'}</Button>
        <Button variant="ghost" busy={update.isPending} onClick={() => update.mutate({ id: endpoint.id, enabled: !endpoint.enabled })}>
          {endpoint.enabled ? 'Turn off' : 'Turn on'}
        </Button>
        <Button
          variant="ghost"
          busy={rotate.isPending}
          onClick={() => {
            if (window.confirm('Make a new signing secret? The old one stops working at once.')) rotate.mutate(endpoint.id, { onSuccess: (made) => onSecret(made.secret) })
          }}
        >
          New secret
        </Button>
        <Button
          variant="ghost"
          busy={remove.isPending}
          onClick={() => {
            if (window.confirm(`Stop sending events to ${endpoint.url}?`)) remove.mutate(endpoint.id)
          }}
        >
          Delete
        </Button>
      </div>
      {error && <ErrorNote error={error} />}
      {open && <Deliveries workspaceId={workspaceId} endpointId={endpoint.id} />}
    </div>
  )
}

function Deliveries({ workspaceId, endpointId }: { workspaceId: string; endpointId: string }) {
  const deliveries = useWebhookDeliveries(workspaceId, endpointId, true)
  if (deliveries.isPending) return <LoadingBlock />
  if (deliveries.isError) return <ErrorNote error={deliveries.error} />
  if (deliveries.data.length === 0) return <p className="muted small">Nothing sent yet.</p>
  return (
    <div className="table-wrap">
      <table className="table">
        <thead><tr><th>Event</th><th>Status</th><th>Attempts</th><th>Sent</th></tr></thead>
        <tbody>
          {deliveries.data.map((delivery) => {
            const status = DELIVERY[delivery.status]
            return (
              <tr key={delivery.id}>
                <td><code>{delivery.event}</code></td>
                <td>
                  <Badge tone={status.tone}>{delivery.response_status ? `${status.label} · ${delivery.response_status}` : status.label}</Badge>
                  {delivery.error && <div className="muted small">{delivery.error}</div>}
                </td>
                <td className="muted">{delivery.attempts}</td>
                <td className="muted">{timeAgo(delivery.created_at)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
